import React, { useEffect, useState } from 'react'
import './AppraisalForm.scss'
import { BreadCrumb } from 'Components'
import { ApStepOne, ApStepThree, ApStepTwo } from 'Pages/Frontend/Components'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { SubmitHandler, useForm } from 'react-hook-form'
import { appraisalService } from 'Services/appraisalService'
import Swal from 'sweetalert2'
import SaveIcon from 'Assets/Images/save-for-later.png'

export type AppraisalFormProps = {
  kpi: {
    metric: string
    target: number
    employee_score: number
    employee_comment?: string
    manager_score?: number
    relevance?: number
  }[]
  coreValues: {
    question: string
    category: string
    employee_score: number
    manager_score?: number
  }[]
  personalAssessment: {
    category: string
    question: string
    answer: string
  }[]
  isDraft?: boolean | undefined
}
const AppraisalForm = () => {
  const [searchParams] = useSearchParams()
  const [request, setRequest] = useState(false)

  // Get a specific query parameter
  const step = searchParams.get('step')

  // const currentStep = step ? parseInt(step) : 1; // Convert step to a number and default to 1 if invalid.

  const [currentStep, setCurrentStep] = useState(1)
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    trigger,
    watch,
    formState: { errors },
    control,
  } = useForm<AppraisalFormProps>()

  const onSubmit: SubmitHandler<AppraisalFormProps> = async (data, e) => {
    // console.log('Form Data:', data)
    const action = (e?.nativeEvent as SubmitEvent)?.submitter as HTMLButtonElement

    const isDraft = action?.value === 'draft'
    const payload = {
      ...data,
      isDraft,
    }
    setRequest(true)
    await appraisalService.create(payload).then(
      () => {
        setRequest(false)
        Swal.fire({
          icon: 'success',
          title: `${isDraft ? 'Saved' : 'Completed'}`,
          text: `Appraisal ${isDraft ? 'saved' : 'submitted'} successfully!`,
        })

        setTimeout(() => {
          navigate('/appraisals')
        }, 2000)
      },
      (error) => {
        setRequest(false)
        Swal.close()
        // console.log('productService error', error.response)
        const swalWithBootstrapButtons = Swal.mixin({
          customClass: {
            cancelButton: 'btn btn-danger',
          },
          buttonsStyling: false,
        })

        swalWithBootstrapButtons.fire({
          title: 'Oops!',
          text: error.response.data.error || error.response.data.message,
          icon: 'warning',
          showCancelButton: true,
          showConfirmButton: false,
          cancelButtonText: 'close',
          reverseButtons: true,
        })
      },
    )
  }

  const handleNext = async () => {
    const isValid = await trigger() // Validate the current form fields
    if (isValid) {
    setCurrentStep((prev) => prev + 1)
    navigate(`/appraisals-form?step=${currentStep + 1}`)
    } else {
      console.error('Form contains errors, cannot proceed to the next step.')
    }
  }

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1)
      navigate(`/appraisals-form?step=${currentStep - 1}`)
    }
  }
  // console.log('Error: ', errors)

  const [settings, setSettings] = useState<{
    id: number
    appraisalYear: number
    appraisalQuarter: number
    appraisalOpen: boolean
    payslipMonth: string
    appraisalClosingDate: string
    payslipYear: any
    createdAt: string
  }>()

  const getAppraisalSettings = async () => {
    await appraisalService.settings().then(
      (res) => {
        // console.log('settings', res.data.data)
        setSettings(res.data.data)
      },
      (error) => {
        console.log(error)
      },
    )
  }

  useEffect(() => {
    getAppraisalSettings()
  }, [])

  const [activeAccordion, setActiveAccordion] = useState(1)
  const [inputError, setInputError] = useState(false)
  return (
    <div className='AppraisalForm me-md-3 mb-5'>
      <div className='container'>
        <BreadCrumb
          title={`Q${settings?.appraisalQuarter} ${settings?.appraisalYear} Appraisal`}
          parent={'Appraisal'}
          parentUrl={'appraisals'}
          showTitle={false}
        />
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='container mt-5'>
          <div className='section-top mb-4'>
            <div className='section-title'>
              Q{settings?.appraisalQuarter} {settings?.appraisalYear} Appraisal Form
            </div>
            <div className='section-description'>SECTION A ({step} of 3)</div>
          </div>
          {currentStep === 1 && (
            <ApStepOne
              register={register}
              errors={errors}
              isManager={false}
              activeAccordion={activeAccordion}
              setActiveAccordion={setActiveAccordion}
              inputError={inputError}
              setInputError={setInputError}
            />
          )}
          {currentStep === 2 && (
            <ApStepTwo
              register={register}
              errors={errors}
              watch={watch}
              isAdmin={false}
              isManager={false}
              isDisabled={false}
              control={control}
            />
          )}
          {currentStep === 3 && (
            <ApStepThree
              register={register}
              errors={errors}
              watch={watch}
              isAdmin={false}
              isManager={false}
            />
          )}

          <div className='row justify-content-end mt-3'>
            <div className='col-sm-12 col-md-6 col-xl-6 profile-action-ap'>
              {currentStep === 3 && (
                <button
                  name='action'
                  value='draft'
                  className='btn m-1 btn-navigate d-flex align-items-center'
                  type='submit'
                  disabled={request}
                >
                  <span className='me-2 fs-5'>
                    {' '}
                    <img className='save-icon' src={SaveIcon} alt='icon' />
                  </span>
                  Save for Later{' '}
                  {request && <span className='spinner-border spinner-border-sm'></span>}
                </button>
              )}
            </div>
            <div className='col-sm-12 col-md-6 col-xl-6 text-end'>
              {currentStep > 1 && (
                <button
                  type='button'
                  className='btn btn-default m-1 btn-navigate'
                  onClick={handlePrev}
                >
                  Prev
                </button>
              )}

              {currentStep < 3 && (
                <button
                  type='button'
                  className='btn btn-default m-1 btn-navigate'
                  onClick={handleNext}
                >
                  Next
                </button>
              )}

              {currentStep === 3 && (
                <button
                  name='action'
                  value='submit'
                  className='btn btn-default m-1 btn-navigate'
                  type='submit'
                  disabled={request}
                >
                  Submit {request && <span className='spinner-border spinner-border-sm'></span>}
                </button>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}

export default AppraisalForm
